import { reviewActionOr, reviewChangesOr, updateElementAudit } from './audit';
import { collectionChanges } from './change-api';
import { Audit, NCNBReason } from './domain';
import { FormApi, formApi } from './form';
import { listApi } from './list';
import { addNCNBReasonsRequest, deleteNCNBReasons, updateNCNBReasonRequest } from './ncnbreasons';
import { WorksheetContext } from './visit-context';
import { isReviewWorkflowState } from './workflow-api';
import { WorksheetFormApi } from './worksheet-form-api';

export interface NcnbReasonsApi extends WorksheetFormApi<NCNBReason> {}

export function ncnbReasonsApi(context: WorksheetContext): NcnbReasonsApi {
  const { worksheet, status, config, ncnbReasons: ncnbReasons, readonly } = context;
  const { worksheetId } = worksheet;

  const ncnbReasonsList = config.ncnbList;

  const defaultFilter = isReviewWorkflowState() ? 'all' : 'active';
  const list = listApi({
    defaultFilter,
    all: ncnbReasons,
    id: (a: NCNBReason) => a.ncnbReasonId,
    equals: (a, b) => a.ncnbReasonId === b.ncnbReasonId,
    active: (v) => v.active,
    setActive: (v) => (v.active = true),
    setInactive: (v) => (v.active = false),
  });

  let editing: NCNBReason;
  function editItem(v: NCNBReason) {
    api.onEdit();
    list.actions.edit(v);

    editing = v;

    form.setValues({
      reason: reason.setListValue((c) => c.description === v.description),
      comment: v.comments,
    });
  }

  function addItem(v: NCNBReason) {
    async function request(update?: (v: any) => void) {
      if (update) {
        update(v);
      }
      v.ncnbReasonId = editing?.ncnbReasonId;
      // if(v.audit?.createdBy?.activities) {
      //   const { activities, ...updatedCreatedBy } = v.audit?.createdBy;
      //   v.audit.createdBy = updatedCreatedBy;
      // }
      

      const response = await (editing ? updateNCNBReasonRequest(v) : addNCNBReasonsRequest(v));

      if (response.success) {
        const item = response.result;

        if (update) {
          update(item);
        }

        if (editing) {
          list.actions.replace(item, (v) => v.ncnbReasonId === editing.ncnbReasonId);
        } else {
          list.actions.add(item);
        }
      } else {
        setStatus((response.errors || []).join(' '));
      }

      editing = null;
    }

    const edit = list.data.getState().editing;

    if (edit) {
      reviewChangesOr(
        {
          title: 'Review Changes For No Code / No Bill',
          original: edit,
          update: v,
          changeReasons: {
            actualCPTCode: config.auditNCNB,
            targetCPTCode: config.auditNCNB,
          },
          fieldNames: ['reason', 'code'],
          onComplete: (a: Audit) => {
            updateElementAudit(v, a);
            request();
          },
          onCancel: () => (editing = null),
        },
        request
      );
    } else {
      reviewActionOr(
        {
          title: 'Add No Code / No Bill',
          list: config.auditNCNB,
          onComplete: (a) => {
            a.action = 'CREATE';
            request((v) => updateElementAudit(v, a));
          },
        },
        request
      );
    }
  }

  function removeItem(v: NCNBReason) {
    async function request() {
      const response = await deleteNCNBReasons({
        worksheetId: worksheet.worksheetId,
        ncnbReasonId: v.ncnbReasonId,
      });
      if (response.success) {
        list.actions.remove(v);
      } else {
        setStatus((response.errors || []).join(' '));
      }
    }
    reviewActionOr(
      {
        title: 'Remove No Code / No Bill ',
        list: config.auditNCNB,
        onComplete: (a) => {
          a.action = 'DELETE';
          v.audit = a;
          request();
        },
      },
      request
    );
  }

  const initialValues = {
    reason: null,
    comment: '',
  };

  const setStatus = status.getState().setStatus;

  const form: FormApi<Partial<NCNBReason>> = formApi({
    disabled: readonly,
    values: initialValues,
    fields: {
      reason: {
        label: 'Reason',
        list: ncnbReasonsList,
        listLabel: (v) => v?.code?.concat(' - ', v.description),
        required: true,
        onEnterValid: () => {
          form.submit();
        },
      },
      comment: {
        label: 'Comments',
        onEnter: () => form.submit(),
      },
    },
    valid: (v) => {
      return !!v.reason
    },
    onSubmit: async (v) => {
      if(!v.reason) return;
      
      addItem({
        worksheetId,
        reasonCode: v.reason.code,
        description: v.reason.description,
        comments: v.comment,
        workflows: v.reason.workflows ||  v.reason.workflow,
        flag: v.reason.flag,
        newAction: v.reason.newAction,
      });
    },
    resetValues: () => initialValues,
  });

  const { reason } = form.controls;

  const listChanges = collectionChanges(list, {
    changeLabel: (v: NCNBReason[]) => v.map((u) => u.ncnbReasonId).join(', '),
  });

  const api = {
    form,
    list,
    listChanges,
    context,
    addItem,
    editItem,
    removeItem,
    onEdit: () => null,
    setPhysician: () => null,
  };

  return api;
}
